import { T, useTheme, dens, Ico, Card, Badge, ChoiceChips } from '../../../design';
import type { ClaimDraft } from '../../../types/claims.types';
import { OUTCOMES, TYPE_LABEL } from '../claimsConfig';
import { Thumb } from './shared';

const MAX_ITEMS = 50;

function FieldLabelInline({ children }: { children: React.ReactNode }) {
  return <span style={{ fontSize: 12.5, fontWeight: 700, color: T.fg }}>{children}</span>;
}
function Spinner() {
  return <span style={{ width: 16, height: 16, border: `2px solid ${T.border}`, borderTopColor: T.accent, borderRadius: 999, display: 'inline-block', animation: 'pp-spin 0.7s linear infinite' }} />;
}

export interface ClaimCreateProps {
  draft: ClaimDraft;
  setDraft: React.Dispatch<React.SetStateAction<ClaimDraft>>;
  onAddItem: () => void;
  onEditItem: (index: number) => void;
  onRemoveItem: (index: number) => void;
  onSubmit: () => void;
  submitting: boolean;
}

export function ClaimCreate({ draft, setDraft, onAddItem, onEditItem, onRemoveItem, onSubmit, submitting }: ClaimCreateProps) {
  const t = useTheme();
  const d = dens(t);
  const valid = !!draft.preferredOutcome && draft.items.length > 0;
  const atMax = draft.items.length >= MAX_ITEMS;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, paddingBottom: 92 }}>
      <div>
        <FieldLabelInline>Ønsket udfald <span style={{ color: T.error }}>*</span></FieldLabelInline>
        <div style={{ marginTop: 7 }}>
          <ChoiceChips columns={2} value={draft.preferredOutcome ?? ''} options={OUTCOMES} onChange={(v) => setDraft((s) => ({ ...s, preferredOutcome: v as ClaimDraft['preferredOutcome'] }))} />
        </div>
      </div>

      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
          <FieldLabelInline>Varelinjer{draft.items.length > 0 && <span style={{ color: T.fg3, fontWeight: 700 }}> · {draft.items.length}</span>}</FieldLabelInline>
          <span style={{ fontSize: 11, color: T.fg3, fontWeight: 600 }}>{atMax ? 'Maks. 50 nået' : 'Op til 50'}</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: d.gap }}>
          {draft.items.map((it, i) => (
            <Card key={i} pad={0} style={{ overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 13 }}>
                <Thumb size={42} src={it.product?.imageUrl} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: T.fg, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.product?.productName}</div>
                  <div style={{ fontSize: 11.5, color: T.fg3, fontWeight: 600, marginTop: 2, display: 'flex', gap: 7, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span>{it.claimType ? TYPE_LABEL[it.claimType] : '—'}</span><span>·</span><span>{it.quantity} stk.</span>
                    {it.isB2c && <Badge tone="neutral">B2C</Badge>}
                    {it.media.length > 0 && <span style={{ display: 'inline-flex', gap: 3, alignItems: 'center' }}><Ico.image size={12} />{it.media.length}</span>}
                  </div>
                </div>
                <button type="button" aria-label="Fjern vare" onClick={() => onRemoveItem(i)} style={{ border: 0, background: 'transparent', color: T.fg3, width: 34, height: 34, borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}><Ico.trash size={17} /></button>
              </div>
              <button type="button" onClick={() => onEditItem(i)} style={{ width: '100%', border: 0, borderTop: `1px solid ${T.borderLight}`, background: 'transparent', color: t.accent, padding: '10px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: T.font }}>Rediger</button>
            </Card>
          ))}
          <button type="button" disabled={atMax} onClick={onAddItem} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '14px', borderRadius: t.radius, border: `1.5px dashed ${T.border}`, background: 'transparent', color: t.accent, fontSize: 14, fontWeight: 700, cursor: atMax ? 'not-allowed' : 'pointer', opacity: atMax ? 0.5 : 1, fontFamily: T.font }}><Ico.plus size={18} />Tilføj vare</button>
        </div>
      </div>

      <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 30, background: T.surface, borderTop: `1px solid ${T.border}`, padding: '12px 14px calc(12px + env(safe-area-inset-bottom))', maxWidth: 440, margin: '0 auto' }}>
        <button
          type="button"
          disabled={!valid || submitting}
          onClick={onSubmit}
          style={{ width: '100%', height: 52, borderRadius: t.radius, border: 0, background: !valid || submitting ? T.disabledBg : t.accent, color: !valid || submitting ? T.disabledFg : T.onAccent, fontSize: 15, fontWeight: 800, fontFamily: T.font, cursor: !valid || submitting ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9 }}
        >
          {submitting ? <Spinner /> : <Ico.checkCircle size={19} />}
          {submitting ? 'Sender…' : 'Opret reklamation'}
        </button>
      </div>
    </div>
  );
}
